import { useState } from 'react';

// Confirmation-dialog state machine. Pairs a target row with an open/close
// pair and a Radix-style `onOpenChange` for `<AlertDialog>` interop.
//
// Replaces the boilerplate trio repeated across admin sections:
//   const [target, setTarget] = useState<T | null>(null);
//   const onOpenChange = (next: boolean) => { if (!next) setTarget(null); };
//   <AlertDialog open={target !== null} onOpenChange={onOpenChange} ... />
//
// Usage:
//   const remove = useConfirmDialog<GroupMemberView>();
//   ...
//   onSelect={() => remove.request(member)}
//   ...
//   <AlertDialog open={remove.open} onOpenChange={remove.onOpenChange} ... />
//
// `confirm` callers read `remove.target` for the captured value and call
// `remove.close()` from the mutation's `onSettled`.

export interface ConfirmDialogState<T> {
  readonly target: T | null;
  readonly open: boolean;
  readonly request: (target: T) => void;
  readonly close: () => void;
  readonly onOpenChange: (next: boolean) => void;
}

export function useConfirmDialog<T>(): ConfirmDialogState<T> {
  const [target, setTarget] = useState<T | null>(null);
  return {
    target,
    open: target !== null,
    request: setTarget,
    close: () => setTarget(null),
    onOpenChange: (next: boolean) => {
      if (!next) setTarget(null);
    },
  };
}
